import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TablePaginationActions from '../molecules/TablePaginationActions';
import {
  SUMMARY_ROWS_PER_PAGE_OPTIONS,
  seriesColumnList,
} from '../../constants';
import { formatCount, formatDisplayedRows } from '../../utils/format';
import type { HealthSystemSummaryGridRow } from '../../interfaces/Dashboard';

interface SummaryTableProps {
  rows: HealthSystemSummaryGridRow[];
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

/**
 * Health-system summary grid: Name, one column per status series, Total
 * Requests. Paginated client-side because the count endpoint returns every row.
 */
const SummaryTable = ({
  rows,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: SummaryTableProps) => {
  const columnCount = seriesColumnList.length + 2;
  const visibleRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <TableContainer className='request-table-list'>
      <Table aria-label='health system summary' size='small'>
        <TableHead className='request-table-list-header'>
          <TableRow>
            <TableCell align='center'>Name</TableCell>
            {seriesColumnList.map((column) => (
              <TableCell key={column.field} align='center'>
                {column.field}
              </TableCell>
            ))}
            <TableCell align='center'>Total Requests</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {visibleRows.map((row, index) => (
            <TableRow
              key={`${row.healthSystemName}-${index}`}
              className='request-table-row'
            >
              <TableCell
                align='center'
                className='request-table-column name capital-text'
              >
                {row.healthSystemName}
              </TableCell>
              {seriesColumnList.map((column) => (
                <TableCell
                  key={column.field}
                  align='center'
                  className='request-table-column name capital-text'
                >
                  {formatCount(row[column.field])}
                </TableCell>
              ))}
              <TableCell
                align='center'
                className='request-table-column name capital-text'
              >
                {/* '' is deliberate here - see toSummaryGridRows - and stays
                    blank rather than becoming "0", unlike the status columns
                    above which have no such distinction to preserve. */}
                {row.totalRequests ? formatCount(row.totalRequests) : ''}
              </TableCell>
            </TableRow>
          ))}

          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} align='center'>
                No Record Found
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>

        {rows.length > 0 ? (
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={SUMMARY_ROWS_PER_PAGE_OPTIONS}
                colSpan={columnCount}
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                labelDisplayedRows={formatDisplayedRows}
                slotProps={{
                  select: {
                    inputProps: { 'aria-label': 'Records per page' },
                    native: true,
                  },
                }}
                onPageChange={(_event, newPage) => onPageChange(newPage)}
                onRowsPerPageChange={(event) =>
                  onRowsPerPageChange(parseInt(event.target.value, 10))
                }
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </TableContainer>
  );
};

export default SummaryTable;
